import { string } from 'yup';

import type { RequestTypeSetting } from '@/types/requests';

import type { AutomationSettings, ResourceOption } from './types';
import { SupportedSettingTypes } from './settingTypes';
import {
  ApplicationSlug,
  GoogleWorkspaceLicenseSettingNames,
} from '@/constants/applications';

/**
 * Converts AutomationSettings to RequestTypeSettingDefaults format.
 * Extracts just the values from the AutomationSettings object.
 */
export const toSettingDefaults = (
  settings: AutomationSettings
): Record<string, string | string[] | null> => {
  return Object.entries(settings).reduce(
    (acc, [key, { value }]) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string | string[] | null>
  );
};

/**
 * Checks if a setting value is empty (empty string, empty array, null, or undefined).
 */
export const isEmptySettingValue = (
  value: string | string[] | null | undefined
): boolean => {
  if (!value) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value === '';
};

/**
 * Checks if all settings in an AutomationSettings object have empty values.
 * Returns true if settings object is empty or all values are empty.
 */
export const areAllSettingsEmpty = (settings: AutomationSettings): boolean => {
  const entries = Object.entries(settings);
  if (entries.length === 0) {
    return true;
  }
  return entries.every(([, { value }]) => isEmptySettingValue(value));
};

/**
 * Builds select options from integration resource data.
 * Handles both string arrays and object arrays with label/value keys.
 */
export const buildOptionsFromResource = (
  resourceLabel: string | number | undefined,
  resourceValue: string | number | undefined,
  resource?: unknown[]
): ResourceOption[] => {
  if (!resource || resource.length === 0) {
    return [];
  }

  if (resource.every(r => typeof r === 'string')) {
    return (resource as string[]).map(r => ({ label: r, value: r }));
  }

  if (resourceLabel !== undefined && resourceValue !== undefined) {
    return resource.map(r => {
      const record = r as Record<string | number, unknown>;
      return {
        label: String(record[resourceLabel]),
        value: String(record[resourceValue]),
      };
    });
  }

  return [];
};

/**
 * Resolves a value to its display label using resource options.
 */
export const resolveValueToLabel = (
  val: string,
  options: ResourceOption[]
): string => {
  const match = options.find(opt => opt.value === val);
  return match?.label ?? val;
};

/**
 * Sorts request type settings by orderValue, then inserts children immediately
 * after their parent setting.
 */
export const sortSettingsWithChildren = (
  settings: RequestTypeSetting[]
): RequestTypeSetting[] => {
  const sorted = [...settings].sort(
    (a, b) => (a.orderValue ?? 0) - (b.orderValue ?? 0)
  );

  const roots: RequestTypeSetting[] = [];
  const childrenByParent = new Map<string, RequestTypeSetting[]>();

  for (const setting of sorted) {
    if (setting.parentRequestTypeSettingId) {
      const parentId = setting.parentRequestTypeSettingId;
      if (!childrenByParent.has(parentId)) {
        childrenByParent.set(parentId, []);
      }
      childrenByParent.get(parentId)!.push(setting);
    } else {
      roots.push(setting);
    }
  }

  const result: RequestTypeSetting[] = [];

  const appendWithDescendants = (setting: RequestTypeSetting) => {
    result.push(setting);
    for (const child of childrenByParent.get(setting.id) ?? []) {
      appendWithDescendants(child);
    }
  };

  for (const root of roots) {
    appendWithDescendants(root);
  }

  return result;
};

/**
 * Filters AutomationSettings to exclude child settings whose parent's current
 * value does not match the required parentSettingValue.
 */
export const filterVisibleSettings = (
  settings: AutomationSettings,
  allRTS: RequestTypeSetting[]
): AutomationSettings => {
  const settingsById = new Map(allRTS.map(s => [s.id, s]));
  const rtsByName = new Map(allRTS.map(s => [s.name, s]));

  const isAncestorChainVisible = (rts: RequestTypeSetting): boolean => {
    if (!rts.parentRequestTypeSettingId || !rts.parentSettingValue) return true;
    const parent = settingsById.get(rts.parentRequestTypeSettingId);
    if (!parent) return true;
    if (settings[parent.name]?.value !== rts.parentSettingValue) return false;
    return isAncestorChainVisible(parent);
  };

  return Object.fromEntries(
    Object.entries(settings).filter(([key]) => {
      const rts = rtsByName.get(key);
      if (!rts) return true;
      return isAncestorChainVisible(rts);
    })
  );
};

/**
 * Returns whether a setting should be visible given the current form values.
 * A setting with a parentSettingValue is hidden unless the parent's current
 * value matches.
 */
export const isSettingVisible = (
  setting: RequestTypeSetting,
  settingsById: Map<string, RequestTypeSetting>,
  currentValuesById: Record<string, string | string[] | null | undefined>
): boolean => {
  if (!setting.parentRequestTypeSettingId || !setting.parentSettingValue) {
    return true;
  }
  const parent = settingsById.get(setting.parentRequestTypeSettingId);
  if (!parent) return true;
  if (currentValuesById[parent.id] !== setting.parentSettingValue) return false;
  return isSettingVisible(parent, settingsById, currentValuesById);
};

const emailSchema = string().email();

/**
 * True when an email-type setting holds a non-empty value that isn't a valid
 * email address. Empty values are valid -- requiredness is checked separately.
 */
export const isInvalidEmailSetting = (
  setting: RequestTypeSetting,
  value: string | string[] | null | undefined
): boolean =>
  setting.type === SupportedSettingTypes.Email &&
  typeof value === 'string' &&
  !isEmptySettingValue(value) &&
  !emailSchema.isValidSync(value);

export const isGoogleLicenseSetting = (
  name: string,
  applicationKey: string
) => {
  return (
    applicationKey === ApplicationSlug.GoogleWorkspace &&
    (Object.values(GoogleWorkspaceLicenseSettingNames) as string[]).includes(
      name
    )
  );
};
